import { createContext, ReactNode, useContext, useState, useMemo, useCallback } from 'react';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import SetGlobalParticipation from './SetGlobalParticipation';

interface GlobalParticipationContextType {
  closeModal: () => void;
  isOpen: boolean;
  openModal: (takerMarkets: TakerMarket[]) => void;
  takerMarkets: TakerMarket[] | null;
}

interface GlobalParticipationProviderProps {
  children: ReactNode;
}

const GlobalParticipationContext = createContext<GlobalParticipationContextType | undefined>(undefined);

export const useGlobalParticipationContext = () => {
  const context = useContext(GlobalParticipationContext);

  if (!context) {
    throw new Error('useGlobalParticipationContext must be used within a GlobalParticipationProvider');
  }

  return context;
};

const GlobalParticipationProvider = ({ children }: GlobalParticipationProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [takerMarkets, setTakerMarkets] = useState<TakerMarket[] | null>(null);

  const openModal = useCallback((takerMarkets: TakerMarket[]) => {
    setTakerMarkets(takerMarkets);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);

    // clear buyer after modal is closed
    setTimeout(() => {
      setTakerMarkets(null);
    }, 300);
  }, []);

  const contextValue = useMemo(
    () => ({
      closeModal,
      isOpen,
      openModal,
      takerMarkets,
    }),
    [closeModal, isOpen, openModal, takerMarkets]
  );

  return <GlobalParticipationContext.Provider value={contextValue}>{children}</GlobalParticipationContext.Provider>;
};

const GlobalParticipationComponent = () => {
  const { closeModal, isOpen, takerMarkets } = useGlobalParticipationContext();

  return takerMarkets ? (
    <SetGlobalParticipation onClose={closeModal} open={isOpen} takerMarkets={takerMarkets} />
  ) : null;
};

export const GlobalParticipationWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <GlobalParticipationProvider>
      <GlobalParticipationComponent />
      {children}
    </GlobalParticipationProvider>
  );
};
